import React from 'react';
import { Lock } from 'lucide-react';
import ProjectSidebarLayout from './ProjectSidebarLayout';
import { useCan, useCurrentRole, roleLabel, type Permission } from '../hooks/usePermissions';

type SidebarSection = React.ComponentProps<typeof ProjectSidebarLayout>['activeSection'];

interface RequirePermissionProps {
  /** The capability the wrapped screen needs end to end. */
  permission: Permission;
  /** Keeps the right sidebar entry highlighted on the "no access" screen. */
  activeSection: SidebarSection;
  /** Title shown on the "no access" screen, e.g. "Automations". */
  title: string;
  /**
   * Overrides the "owners and admins" line for screens that aren't gated that
   * way — the lead-desk scoping, where the screen is hidden from LEAD_MANAGER
   * but open to everyone else. The role sentence is appended either way.
   */
  reason?: string;
  children: React.ReactElement;
}

/**
 * Route-level gate for screens a role has no business rendering: either the
 * backend controller is guarded end to end (channels, automations, logs — the
 * reads 403 too, so the screen would only produce a wall of failed requests),
 * or the screen is scoped out of the role's surface entirely (the agent
 * builder, voice agent, settings, Users and Support Tickets for LEAD_MANAGER —
 * pass `reason` for those, since the default copy names the wrong roles).
 *
 * Nav items for these screens are already hidden in ProjectSidebarLayout; this
 * is what catches a typed URL or a stale bookmark. It renders inside the app
 * shell on purpose, so the user lands somewhere they can navigate out of
 * rather than on a dead end.
 *
 * Like every check in usePermissions, this is UX, not security — the backend
 * is still the authority (see FRONTEND_RBAC_GUIDE.md §3).
 */
const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  activeSection,
  title,
  reason,
  children,
}) => {
  const allowed = useCan(permission);
  const role = useCurrentRole();

  if (allowed) return children;

  return (
    <ProjectSidebarLayout activeSection={activeSection} pageTitle={title}>
      <div className="container">
        <div className="card empty-state">
          <div className="empty-state-icon">
            <Lock size={28} />
          </div>
          <h2 className="empty-state-title">You don&apos;t have access to {title}</h2>
          <p className="empty-state-desc">
            {reason ?? `${title} is limited to organization owners and admins.`}
            {role ? ` Your role is ${roleLabel(role)}.` : ''} Ask an owner in your organization if
            you need access.
          </p>
        </div>
      </div>
    </ProjectSidebarLayout>
  );
};

export default RequirePermission;
